import { useState, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import { getStudioLiveTitleOverview, updateStudioLiveTitleOverview } from '@sg/services/globalpedia';
import {
  getFormatStudioProjectDatas,
  getSortProjectDatas,
  generateMonthColumns,
} from '@sg/pages/StudioManagement/PortfolioOverview/utils';
import type { IPipelineProject } from '@sg/pages/StudioManagement/PortfolioOverview/components/PipelineCalendar/type';
import type { TOverviewType } from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/type';
import { getFormatNotes } from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/utils';
import { TYPES } from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/const';

export const useLiveVisualizationAll = () => {
  const [selectedMonth, setSelectedMonth] = useState<any>(
    moment().subtract(1, 'month').startOf('month'),
  );
  // 级联筛选器当前选中值
  const [cascadeValue, setCascadeValue] = useState<string[]>([]);

  const [projects, setProjects] = useState<IPipelineProject[]>([]);
  const [projectsMC, setProjectsMC] = useState<IPipelineProject[]>([]);
  const [projectsLO, setProjectsLO] = useState<IPipelineProject[]>([]);
  const [isError, setIsError] = useState(false);
  const [summaryDataMC, setSummaryDataMC] = useState<any>({});
  const [summaryDataLO, setSummaryDataLO] = useState<any>({});
  const projectsMCRef = useRef<IPipelineProject[]>([]);
  const projectsLORefOrder = useRef<IPipelineProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [loFilterLoading, setLoFilterLoading] = useState(false);
  const [notesContent, setNotesContent] = useState<string>('');
  const [studioProjectList, setStudioProjectList] = useState<any[]>([]);
  const [projectOrderList, setProjectOrderList] = useState<any[]>([]);
  const [activeOverview, setActiveOverview] = useState<TOverviewType>('mc');
  const [notes, setNotes] = useState<any[]>([]);
  const initialNotesRef = useRef<any[]>([]);
  const initialProjectOrderRef = useRef<any[]>([]);

  const monthColumns = useMemo(() => generateMonthColumns(selectedMonth), [selectedMonth]);

  const turnDate = (date: any) => {
    return date ? moment(date).format('YYYY-MM')
      : moment().startOf('month').format('YYYY-MM');
  };

  const fetchData = async (selectedMonthValue: string) => {
    setLoading(true);
    const selectedMonthStr = turnDate(selectedMonthValue);

    try {
      const [mcRes, loRes] = await Promise.all([
        getStudioLiveTitleOverview({
          type: TYPES.mc,
          datatime: selectedMonthStr,
        }),
        getStudioLiveTitleOverview({
          type: TYPES.lo,
          datatime: selectedMonthStr,
        }),
      ]);

      const resDataMC: any = mcRes;
      const resDataLO: any = loRes;
      const projectListMC = resDataMC?.list || [];
      const projectListLO = resDataLO?.list || [];
      const summaryDaMC = resDataMC?.summary || {};
      const summaryDaLo = resDataLO?.summary || {};

      setProjectsMC(projectListMC);
      setSummaryDataMC(summaryDaMC);
      setProjectsLO(projectListLO);
      setSummaryDataLO(summaryDaLo);
      projectsMCRef.current = projectListMC;
      projectsLORefOrder.current = projectListLO;

      // 排序用
      const projectOrderListTemp = getSortProjectDatas(projectListMC);
      setProjectOrderList(projectOrderListTemp);
      initialProjectOrderRef.current = projectOrderListTemp;

      // 筛选器用
      const proStudiosDatas = resDataMC?.studios || [];
      const allProject = getFormatStudioProjectDatas(proStudiosDatas);
      setStudioProjectList(allProject);

      const formattedNotes = getFormatNotes([...projectListMC, ...projectListLO], selectedMonthStr);
      setNotes(formattedNotes);
      initialNotesRef.current = formattedNotes;

      setIsError(false);

      changeFilterProjects(cascadeValue, activeOverview, projectListMC, projectListLO, allProject, selectedMonthValue);
    } catch (error) {
      setProjectsMC([]);
      setSummaryDataMC({});
      setProjectsLO([]);
      setSummaryDataLO({});
      // setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const newParams: any = {};
      const hasOrderChanges = JSON.stringify(projectOrderList || [])
        !== JSON.stringify(initialProjectOrderRef.current || []);
      if (projectOrderList?.length > 0 && hasOrderChanges) {
        // 排序提交格式: { orders: [{ game_title, order }, ...] }
        newParams.orders = projectOrderList;
      }
      const baselineMap = new Map<string, { note: string; attach: any[] }>();
      const normalizeAttachForCompare = (attach: any) => {
        let parsed = attach;
        if (typeof parsed === 'string' && parsed.trim()) {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            parsed = [];
          }
        }
        if (!Array.isArray(parsed)) {
          return [];
        }
        return parsed
          .map((f: any) => ({
            name: f?.name || f?.file || '',
            path: f?.path || f?.url || '',
          }))
          .filter((f: any) => f?.name || f?.path)
          .sort((a: any, b: any) => `${a?.name}-${a?.path}`.localeCompare(`${b?.name}-${b?.path}`));
      };

      (initialNotesRef.current || []).forEach((n: any) => {
        const title = String(n?.game_title || '');
        if (!title) {
          return;
        }
        baselineMap.set(title, {
          note: n?.note || '',
          attach: normalizeAttachForCompare(n?.attach),
        });
      });

      const formattedNotes = (notes || [])
        .map((n: any) => {
          const title = String(n?.game_title || '');
          if (!title) {
            return null;
          }
          const currentAttach = normalizeAttachForCompare(n?.attach);
          const baseline = baselineMap.get(title);
          const changed = !baseline
            || baseline.note !== (n?.note || '')
            || JSON.stringify(baseline.attach) !== JSON.stringify(currentAttach);
          if (!changed) {
            return null;
          }
          return {
            game_title: n?.game_title,
            date: turnDate(n?.date),
            note: n?.note || '',
            url: Array.isArray(n?.attach)
              ? n.attach.map((f: any) => ({
                name: f?.name,
                path: f?.path,
              }))
              : [],
          };
        })
        .filter(Boolean);
      if (formattedNotes?.length > 0) {
        newParams.notes = formattedNotes;
      }

      if (!newParams.orders && !newParams.notes) {
        setLoading(false);
        return;
      }

      await updateStudioLiveTitleOverview(newParams);
      // 更新成功后，重新请求接口，刷新数据
      await fetchData(selectedMonth);
    } catch (error) {
      setLoading(false);
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedMonth);
  }, []);

  const handleDateFilterChange = (selectedMonthValue: string) => {
    fetchData(selectedMonthValue);
  };

  // 项目拖拽排序：保留完整 project 对象，附 order 字段，未在可视集合中的项目放在末尾
  const handleProjectsReorder = (reorderedProjects: any[]) => {
    const updated = (reorderedProjects || []).map((project, index) => ({
      ...project,
      order: index + 1,
    }));

    // 同步可视项目（用于当前列表渲染）
    setProjects([...updated]);

    // 同步底层分类源（保留过滤场景下未在可视集合中的项目）
    const sourceList = activeOverview === 'mc' ? projectsMC : projectsLO;
    const visibleTitles = new Set(updated.map((p: any) => p?.game_title));
    const others = (sourceList || []).filter(
      (p: any) => !visibleTitles.has(p?.game_title),
    );
    const merged = [...updated, ...others].map((p: any, i: number) => ({
      ...p,
      order: i + 1,
    }));

    // if (activeOverview === 'mc') {
    //   setProjectsMC(merged);
    // } else {
    //   setProjectsLO(merged);
    // }

    // 维护提交格式: { orders: [{ game_title, order }, ...] }
    setProjectOrderList(
      merged.map((p: any) => ({
        game_title: p?.game_title,
        order: p?.order,
      })),
    );
  };

  const handleProjectsReorderCancel = () => {
    const sourceList = activeOverview === 'mc' ? projectsMC : projectsLO;
    setProjectOrderList(getSortProjectDatas(sourceList || []));
    setProjectsMC([...projectsMC]);
    setProjectsLO([...projectsLO]);

    changeFilterProjects();
  };

  const handleActiveOverview = (overview: string) => {
    setActiveOverview(overview as TOverviewType);
    changeFilterProjects(cascadeValue, overview as TOverviewType);
  };

  /**
   * Re-fetch Live-Ops summary card data with a filtered game_titles list.
   * Called when the Studio/Project cascade filter changes so the LO card
   * reflects only the selected studios/projects.
   */
  const fetchLODataByFilter = async (
    cascadeValueTemp: string[],
    allProjectTemp: any[],
    selectedMonthValue: any,
  ) => {
    const selectedMonthStr = turnDate(selectedMonthValue);

    // Resolve project_id[] → game_title[] using the studioProjectList lookup
    const idToTitle = new Map<string, string>();
    (allProjectTemp || []).forEach((studio: any) => {
      (studio?.projects || []).forEach((p: any) => {
        if (p?.project_id) idToTitle.set(p.project_id, p?.game_title);
      });
    });

    const isAll =
      !cascadeValueTemp ||
      cascadeValueTemp.includes('all') ||
      cascadeValueTemp.length === 0;

    const gameTitles = isAll
      ? undefined
      : (cascadeValueTemp.map((v) => idToTitle.get(v)).filter(Boolean) as string[]);

    setLoFilterLoading(true);
    try {
      const loRes: any = await getStudioLiveTitleOverview({
        type: TYPES.lo,
        datatime: selectedMonthStr,
        ...(gameTitles ? { game_titles: gameTitles } : {}),
      });
      setSummaryDataLO(loRes?.summary || {});
      const newProjectListLO = loRes?.list || [];
      setProjectsLO(newProjectListLO);
      projectsLORefOrder.current = newProjectListLO;
      changeFilterProjects(cascadeValueTemp, TYPES.lo as TOverviewType, projectsMCRef.current, newProjectListLO, allProjectTemp, selectedMonthValue);
    } catch {
      // Keep existing data on error — don't blank the card
    } finally {
      setLoFilterLoading(false);
    }
  };

  const changeFilterProjects = (
    cascadeValueTemp = cascadeValue,
    activeOverviewTemp = activeOverview,
    projectListMCTemp = projectsMC,
    projectListLOTemp = projectsLO,
    allProjectTemp = studioProjectList,
    selectedMonthValueTemp = selectedMonth,
  ) => {
    // Miniclip data is not driven by the studio/project dropdown (Miniclip studio
    // is no longer present in the filter options). Always show the full MC list
    // directly from the backend response; only Live-Ops projects get filtered.
    const isMC = activeOverviewTemp === 'mc';
    const filtered = (isMC ? projectListMCTemp : projectListLOTemp) || [];

    let projectsTemp = [...filtered];
    let projectsNotesTemp = [...projectListMCTemp, ...projectListLOTemp];

    const shouldFilter =
      !isMC &&
      !(cascadeValueTemp?.includes('all') || cascadeValueTemp?.length === 0);

    if (shouldFilter) {
      // values 为 project_id 数组（studio_id + '_' + game_title）
      // 通过 studioProjectList 映射 project_id → game_title
      const idToTitle = new Map<string, string>();
      (allProjectTemp || []).forEach((studio: any) => {
        (studio?.projects || []).forEach((p: any) => {
          if (p?.project_id) {
            idToTitle.set(p.project_id, p?.game_title);
          }
        });
      });

      const gameTitles = cascadeValueTemp?.map((v) => idToTitle.get(v))?.filter(Boolean) as string[];
      const newProjectList = filtered?.filter((project: any) => {
        return gameTitles?.includes(project?.game_title);
      });
      projectsTemp = [...(newProjectList || [])];
      const newProjectNotesList = [...projectsNotesTemp]?.filter((project: any) => {
        return gameTitles?.includes(project?.game_title);
      });
      projectsNotesTemp = [...(newProjectNotesList || [])];
    }

    setNotes(getFormatNotes(projectsNotesTemp, selectedMonthValueTemp));
    setProjects([...projectsTemp]);
  };

  return {
    projects,
    allProjects: projects,
    monthColumns,
    loading,
    loFilterLoading,
    notesContent,
    handleProjectsReorder,
    handleProjectsReorderCancel,
    handleDateFilterChange,
    studioProjectList,
    handleConfirm,
    setNotesContent,

    summaryDataMC,
    summaryDataLO,
    isError,
    notes,
    setNotes,
    setProjects,
    setProjectOrderList,
    activeOverview,
    setActiveOverview,
    handleActiveOverview,
    projectsMC,
    projectsLO,
    selectedMonth,
    setSelectedMonth,
    cascadeValue,
    setCascadeValue,
    projectOrderList,
    changeFilterProjects,
    fetchLODataByFilter,
  };
};
